import React, { useState, useEffect } from 'react';
import { 
  Link2, RefreshCw, CheckCircle, 
  Loader2, Inbox 
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { auth } from '../../utils/auth';
import { Button } from '../ui/button';
import { useDarkMode } from '../../context/DarkModeContext';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Google Logo SVG
const GoogleLogo = () => (
  <svg width="24" height="24" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
  </svg>
);

const GoogleIntegration = ({ onConnectionChange }: { onConnectionChange?: (status: boolean) => void }) => {
  const { isDarkMode } = useDarkMode();
  const navigate = useNavigate();
  const [status, setStatus] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    fetchStatus();
    
    // Check for OAuth callback result
    const urlParams = new URLSearchParams(window.location.search);
    const provider = urlParams.get('provider');
    const connected = urlParams.get('connected');
    const newUser = urlParams.get('new_user');
    
    if (provider === 'google' && connected === 'true') {
      toast.success('✅ Google tilkoblet!');
      window.history.replaceState({}, document.title, window.location.pathname);
      fetchStatus();
    } else if (provider === 'google' && !newUser) {
      // Old login flow
      toast.success('✅ Google tilkoblet!');
      window.history.replaceState({}, document.title, window.location.pathname);
      fetchStatus();
    }
  }, [navigate]);

  const fetchStatus = async () => {
    try {
      const token = auth.getToken();
      if (!token) {
        setStatus({ connected: false });
        setLoading(false);
        return;
      }
      
      const headers = { Authorization: `Bearer ${token}` };
      
      // Check calendar status (this also checks if google_tokens exist)
      const response = await axios.get(`${API}/google/calendar/status`, { headers });
      setStatus(response.data);
      
      if (onConnectionChange) {
        onConnectionChange(response.data?.connected || false);
      }
    } catch (error: any) {
      console.error('Error fetching Google status:', error);
      setStatus({ connected: false });
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = () => {
    setConnecting(true);
    // Get current user ID from localStorage
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        // Redirect to Google OAuth connect endpoint with user_id
        window.location.href = `${API}/auth/google/connect?user_id=${user.id}`;
      } catch (e) {
        console.error('Failed to parse user:', e);
        toast.error('Kunne ikke starte tilkobling. Prøv å logge inn på nytt.');
        setConnecting(false);
      }
    } else {
      toast.error('Du må være logget inn for å koble til Google.');
      setConnecting(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const token = auth.getToken();
      const headers = { Authorization: `Bearer ${token}` };
      
      // Fetch latest emails and calendar events
      const [mailRes, calRes] = await Promise.all([
        axios.get(`${API}/google/mail/messages?max_results=10`, { headers }).catch(() => null),
        axios.get(`${API}/google/calendar/events`, { headers }).catch(() => null)
      ]);
      
      const mailCount = mailRes?.data?.count || 0;
      const eventCount = calRes?.data?.count || 0;
      
      toast.success(`✅ Synkronisert: ${mailCount} e-poster, ${eventCount} kalenderhendelser`);
      fetchStatus();
    } catch (error) {
      console.error('Error syncing Google:', error);
      toast.error('Kunne ikke synkronisere Google-data');
    } finally {
      setSyncing(false);
    }
  };

  const isConnected = status?.connected;
  const connectedEmail = status?.email;

  if (loading) {
    return (
      <div className={`rounded-xl p-4 h-full ${
        isDarkMode 
          ? 'bg-white/5 border border-white/10' 
          : 'bg-white border border-gray-200'
      }`}>
        <div className="flex items-center justify-center py-4">
          <Loader2 className="w-6 h-6 animate-spin text-red-500" />
        </div>
      </div>
    );
  }

  // Compact card view for grid layout
  return (
    <div className={`rounded-xl overflow-hidden h-full ${
      isDarkMode 
        ? 'bg-white/5 border border-white/10' 
        : 'bg-white border border-gray-200'
    }`}>
      {/* Header */}
      <div className="bg-gradient-to-r from-red-500 via-yellow-500 to-green-500 p-4 text-white">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center flex-shrink-0">
            <GoogleLogo />
          </div>
          <div className="min-w-0">
            <h3 className="font-bold text-base">Google Workspace</h3>
            <p className="text-white/90 text-xs truncate">
              {isConnected ? connectedEmail : 'Gmail & Kalender'}
            </p>
          </div>
          {isConnected && (
            <CheckCircle className="w-5 h-5 ml-auto flex-shrink-0" />
          )}
        </div>
      </div>

      {/* Content */}
      <div className="p-4" style={{ position: 'relative', zIndex: 100 }}>
        {isConnected ? (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm">
              <span className="w-2 h-2 bg-green-500 rounded-full"></span>
              <span className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>Tilkoblet</span>
            </div>
            
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleSync();
                }}
                disabled={syncing}
                className="inline-flex items-center justify-center px-3 py-1.5 text-xs font-medium rounded-md bg-gradient-to-r from-red-500 to-yellow-500 text-white hover:from-red-600 hover:to-yellow-600 disabled:opacity-50 transition-all"
                style={{ position: 'relative', zIndex: 100, pointerEvents: 'auto' }}
              >
                {syncing ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                <span className="ml-1">{syncing ? 'Synker...' : 'Synk'}</span>
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  navigate('/lexy/dashboard?tab=inbox');
                }}
                className={`inline-flex items-center justify-center px-3 py-1.5 text-xs font-medium rounded-md border transition-all ${
                  isDarkMode 
                    ? 'border-white/20 text-white hover:bg-white/10' 
                    : 'border-gray-300 text-gray-700 hover:bg-gray-100'
                }`}
                style={{ position: 'relative', zIndex: 100, pointerEvents: 'auto' }}
              >
                <Inbox className="w-3 h-3 mr-1" />
                Innboks
              </button>
            </div>
          </div>
        ) : (
          <div className="text-center">
            <p className={`text-xs mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Synk e-post og kalender
            </p>
            <Button
              onClick={handleConnect}
              disabled={connecting}
              size="sm"
              className="w-full bg-gradient-to-r from-red-500 to-green-500 text-white"
            >
              {connecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
              <span className="ml-2">{connecting ? 'Kobler til...' : 'Koble til'}</span>
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default GoogleIntegration;
